import { Box, Stack } from "@mui/material";

import DetailedViewPageHeader from "@/App/Layouts/PageHeader/DetailedViewPageHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import { useOnboardingContext } from "@/common/contexts/onboarding";
import { RoutingHelper } from "@/common/helpers/routing";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { ViewVariant } from "@/common/ts/layout";
import { appCommonConfig } from "@/config/config";
import { EntityType, OnboardingDto } from "@/core/api/generated/v1";

import AppButton from "../../../Button/AppButton";
import AppIcon from "../../../Icons/AppIcon";
import { AppModalCloseIconButton } from "../../../Modals/AppModalCloseIconButton";
import AppTypography from "../../../Text/AppTypography";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import { OnboardingIsShowOnLaunchInput } from "../OnboardingIsShowOnLaunchInput";

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

interface Props extends BaseEntityViewInheritableProps<OnboardingDto> {
  displayProps?: Partial<typeof defaultDisplayProps>;
}

export function OnboardingCompletedView({ displayProps, onClose }: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const profile = useUserProfile();
  const onboardingContext = useOnboardingContext();

  return (
    <BaseEntityView
      entityType={EntityType.Onboarding}
      entityId={onboardingContext.onboarding?.id}
      entity={onboardingContext.onboarding}
      entityRequest={undefined}
      isIniting={onboardingContext.isIniting || !onboardingContext.onboarding}
    >
      {onboardingContext.onboarding && (
        <ViewLayout
          displayProps={displayProps}
          header={
            displayProps?.header && (
              <>
                <DetailedViewPageHeader
                  image={undefined}
                  title={
                    <>
                      Congratulations
                      {profile?.personName?.name ? <>, {profile?.personName?.name}</> : null}!
                    </>
                  }
                  primaryActions={
                    displayProps?.actions && (
                      <Stack direction='row' sx={{ alignItems: "center" }}>
                        <OnboardingIsShowOnLaunchInput />
                      </Stack>
                    )
                  }
                  closeAction={
                    displayProps.closeInHeader && onClose ? (
                      <AppModalCloseIconButton onClick={onClose} />
                    ) : undefined
                  }
                />
              </>
            )
          }
        >
          <ViewContentBlock
            sx={{
              minHeight: "100%",
              flex: 1,
              borderRadius: (th) => th.shapeCustom.borderRadiusCardLarge,
            }}
          >
            <Stack
              sx={{
                minHeight: "100%",
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Stack sx={{ maxWidth: 600, alignItems: "center", textAlign: "center" }}>
                <AppTypography sx={{ fontWeight: 600 }} variant='h3'>
                  <Box>Congratulations,</Box>
                  <Box>
                    {`you've just completed onboarding for`}{" "}
                    <Box sx={{ color: (th) => th.palette.primary.main }} component='span'>
                      {appCommonConfig.appName}
                    </Box>
                    .
                  </Box>
                </AppTypography>

                <AppTypography variant='h6' sx={{ mt: 3 }}>
                  <Box sx={{ fontWeight: 400 }} component='span'>
                    You can always return to this flow from your profile. Wishing you a great
                    experience!
                  </Box>
                </AppTypography>

                <AppButton
                  sx={{ mt: 6, minWidth: 220 }}
                  variant='contained'
                  size='large'
                  color='primary'
                  endIcon={<AppIcon of='arrowForward' />}
                  onClick={() => {
                    history.push(RoutingHelper.getCurrentAppAreaRootPathOrDefault());
                  }}
                >
                  Start work
                </AppButton>
              </Stack>
            </Stack>
          </ViewContentBlock>
        </ViewLayout>
      )}
    </BaseEntityView>
  );
}
